import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

/**
 * AppleSignInButton — renders the Apple "Continue with Apple" button using Sign in with Apple JS.
 *
 * Auto-detects whether Apple Sign-In is configured on the backend by calling /api/auth/apple/config.
 * Returns null until config confirms it's enabled. Lazily loads the Apple JS SDK on demand.
 *
 * Backend env required for this to activate:
 *   APPLE_CLIENT_ID       (Services ID, e.g. com.vyrti.web)
 *   APPLE_TEAM_ID
 *   APPLE_KEY_ID
 *   APPLE_PRIVATE_KEY     (.p8 file contents)
 */
export default function AppleSignInButton({ label = "Continue with Apple" }) {
  const { loginWithApple } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const initedRef = useRef(false);

  useEffect(() => {
    let active = true;
    api.get("/auth/apple/config")
      .then(({ data }) => { if (active) setConfig(data); })
      .catch(() => { if (active) setConfig({ enabled: false }); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!config?.enabled || initedRef.current) return;
    initedRef.current = true;
    const SRC = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

    const init = () => {
      if (!window.AppleID) return;
      try {
        window.AppleID.auth.init({
          clientId: config.client_id,
          scope: "name email",
          redirectURI: window.location.origin + "/auth/apple/callback",
          usePopup: true,
        });
        setSdkReady(true);
      } catch (e) { /* ignore */ }
    };

    const existing = document.querySelector(`script[src="${SRC}"]`);
    if (existing) {
      if (window.AppleID) init();
      else existing.addEventListener("load", init, { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = SRC;
    script.async = true;
    script.onload = init;
    document.body.appendChild(script);
  }, [config]);

  if (!config?.enabled) return null;

  const handleClick = async () => {
    if (!window.AppleID || !sdkReady || busy) return;
    setBusy(true);
    try {
      const response = await window.AppleID.auth.signIn();
      const idToken = response?.authorization?.id_token;
      if (!idToken) throw new Error("Apple didn't return an identity token");
      await loginWithApple(idToken);
      toast.success("Signed in with Apple");
      navigate("/", { replace: true });
    } catch (e) {
      // User-cancellation throws an error; ignore silently for those
      if (e?.error === "popup_closed_by_user" || e?.error === "user_cancelled_authorize") {
        // no-op
      } else {
        toast.error(e?.response?.data?.detail || e?.message || "Apple sign-in failed");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!sdkReady || busy}
      className="btn-block w-full !py-3 bg-black text-white hover:bg-zinc-900 disabled:opacity-60 disabled:cursor-not-allowed"
      data-testid="apple-signin-button"
    >
      <svg className="w-4 h-4" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
      </svg>
      {label}
    </button>
  );
}
